<script lang="ts">
  import clsx from 'clsx';
  import { getExternalLinkAttributes } from '@athena-lib/Link';
  import { objectKeys } from '@athena-lib/object-utils';
  import { getVariantClassName, type ButtonProps } from './types';
  let {
    onClick,
    children,
    class: className,
    full,
    wide,
    size = 'medium',
    element = 'button',
    color = 'brand',
    link,
    action = () => undefined,
    actionArg,
    type,
    disabled,
    ariaLabel,
    'data-initialfocus': initialFocus,
    unstyled,
    title,
  }: ButtonProps = $props();
  const elementProps = $derived(
    element === 'button'
      ? { type: type ?? 'button', disabled: disabled }
      : element === 'a'
        ? {
            href: link?.href,
            ...(link?.isExternal ? getExternalLinkAttributes() : {}),
          }
        : {},
  );

  const sizeClass = $derived.by(() => {
    const sizeClasses: Record<string, string> = {
      small: clsx('py-2 text-sm', !wide && 'px-5', wide && 'px-7'),
      medium: clsx('text-md py-4', !wide && 'px-7', wide && 'px-11'),
      large: clsx('py-5 text-xl', !wide && 'px-11', wide && 'px-20'),

      md_small: clsx('md:py-2 md:text-sm', !wide && 'md:px-5'),
      md_medium: clsx('md:text-md md:py-4', !wide && 'md:px-7'),
      md_large: clsx('md:py-5 md:text-xl', !wide && 'md:px-11'),

      lg_small: clsx('lg:py-2 lg:text-sm', !wide && 'lg:px-5'),
      lg_medium: clsx('lg:text-md lg:py-4', !wide && 'lg:px-7'),
      lg_large: clsx('lg:py-5 lg:text-xl', !wide && 'lg:px-11'),

      circle: clsx('rounded-full py-2 text-sm', !wide && 'px-2', wide && 'px-5'),
    };

    if (typeof size === 'string') {
      return sizeClasses[size];
    } else {
      const classes: string[] = [];
      for (const deviceName of objectKeys(size)) {
        const deviceSize = size[deviceName]!;
        if (deviceName === 'sm') {
          classes.push(sizeClasses[deviceSize]);
        } else {
          classes.push(sizeClasses[deviceName + '_' + deviceSize]);
        }
      }
      return clsx(classes);
    }
  });

  function elementAction(node: HTMLDivElement) {
    if (action) {
      return action(node, actionArg!);
    }
  }
</script>

<svelte:element
  this={element}
  use:elementAction
  role="button"
  tabindex="0"
  class={clsx(
    !unstyled
      ? [
          size == 'circle' ? 'rounded-full' : 'rounded-lg',
          'text-st-black',
          'text-center',
          'inline-block',
          'text-nowrap',
          'font-black',
          'disabled:cursor-not-allowed',
          size !== 'content' && size !== 'circle' && 'min-w-40',
          sizeClass,
          full && 'w-full',
          getVariantClassName(color),
          'outline-st-pink-500',
        ]
      : [],
    className,
  )}
  {...elementProps}
  data-initialfocus={initialFocus}
  onclick={onClick}
  aria-label={ariaLabel}
  {title}
>
  {@render children()}
</svelte:element>
